import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EquipmentTypes } from '@d-orca/shared/trusstor';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  setEquipmentFilters,
  setManagerFilters,
  setProfessionFilters,
} from '@store/slices/monitor.slice';
import {
  selectManagers,
  selectProfessions,
  selectSelectedEquipmentTypes,
  selectSelectedManagerIds,
  selectSelectedProfessionIds,
} from '@store/selectors/monitor.selectors';

/**
 * URL ↔ Redux mirror for the three LeftRail filters.
 *
 * URL is the source of intent (deep links, copy-paste, back/forward).
 * Toggle setters write the URL; the watcher dispatches into Redux.
 *
 * URL tokens are human-readable, NOT internal Mongo `_id`s — those would
 * leak Trusstor's data shape into a user surface (URLs get shared, copied,
 * pasted into tickets). Translation happens here, at the boundary; the
 * Redux state and selectors continue to operate on `_id`s.
 *
 * Param shape (comma-delimited; empty list omits the key):
 *   ?prof=<tradeId>[:<specialty|contractor|n>],…
 *   ?mgr=<name>[:<role|n>],…
 *   ?eq=<EquipmentTypes>,…
 *
 * Disambiguation suffixes are only emitted when the primary token would
 * collide within the project's data set.
 */

const PARAM_PROF = 'prof';
const PARAM_MGR = 'mgr';
const PARAM_EQ = 'eq';

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').filter(Boolean);
}

function arraysEqual(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Token <-> id translation
// ---------------------------------------------------------------------------

type TokenMap = {
  /** id (Mongo `_id` or fallback row id) → URL token */
  idToToken: Map<string, string>;
  /** URL token → id */
  tokenToId: Map<string, string>;
};

type ProfRow = {
  _id?: string;
  tradeId?: string;
  specialty?: string;
  contractor?: string;
};

/**
 * Build a stable id↔token mapping for professions.
 *  - Unique `tradeId` → token is just `<tradeId>`.
 *  - Collision → suffix with first available `:specialty`, `:contractor`,
 *    or `:<index>` (sorted by `_id` for determinism).
 */
function buildProfTokenMap(professions: readonly ProfRow[]): TokenMap {
  const byTradeId = new Map<string, ProfRow[]>();
  for (const p of professions) {
    if (!p._id || !p.tradeId) continue;
    const list = byTradeId.get(p.tradeId);
    if (list) list.push(p);
    else byTradeId.set(p.tradeId, [p]);
  }
  const idToToken = new Map<string, string>();
  const tokenToId = new Map<string, string>();
  for (const [tradeId, group] of byTradeId) {
    const sorted = [...group].sort((a, b) => (a._id ?? '').localeCompare(b._id ?? ''));
    if (sorted.length === 1) {
      const p = sorted[0]!;
      const id = p._id!;
      idToToken.set(id, tradeId);
      tokenToId.set(tradeId, id);
      continue;
    }
    for (let i = 0; i < sorted.length; i++) {
      const p = sorted[i]!;
      const id = p._id!;
      const candidates: string[] = [];
      if (p.specialty) candidates.push(p.specialty);
      if (p.contractor) candidates.push(p.contractor);
      candidates.push(String(i));
      let token = `${tradeId}:${id}`; // safety fallback (always unique)
      for (const c of candidates) {
        const tok = `${tradeId}:${c}`;
        if (!tokenToId.has(tok)) {
          token = tok;
          break;
        }
      }
      idToToken.set(id, token);
      tokenToId.set(token, id);
    }
  }
  return { idToToken, tokenToId };
}

type MgrRow = { _id?: string; name?: string; tradeGroup?: string; disconnected?: boolean };

/**
 * Manager mapping.
 *  - Row id is `_id ?? name` (mirrors `selectManagerListRows`).
 *  - Token is the manager's name; collisions disambiguate with `:role`
 *    (tradeGroup) or `:<index>`.
 *
 * NOTE: we deliberately include `disconnected` managers here. Otherwise a
 * manager going offline would orphan their URL token, the watcher would
 * dispatch a smaller id list, and the user's filter selection would
 * silently shrink. The LeftRail row list (`selectManagerListRows`) keeps
 * filtering disconnected; the *filter persistence* lives here.
 */
function buildMgrTokenMap(managers: readonly MgrRow[]): TokenMap {
  const byName = new Map<string, MgrRow[]>();
  for (const m of managers) {
    if (!m.name) continue;
    const list = byName.get(m.name);
    if (list) list.push(m);
    else byName.set(m.name, [m]);
  }
  const idToToken = new Map<string, string>();
  const tokenToId = new Map<string, string>();
  for (const [name, group] of byName) {
    const sorted = [...group].sort((a, b) => (a._id ?? a.name ?? '').localeCompare(b._id ?? b.name ?? ''));
    if (sorted.length === 1) {
      const m = sorted[0]!;
      const id = m._id ?? m.name!;
      idToToken.set(id, name);
      tokenToId.set(name, id);
      continue;
    }
    for (let i = 0; i < sorted.length; i++) {
      const m = sorted[i]!;
      const id = m._id ?? m.name!;
      const candidates: string[] = [];
      if (m.tradeGroup) candidates.push(m.tradeGroup);
      candidates.push(String(i));
      let token = `${name}:${id}`;
      for (const c of candidates) {
        const tok = `${name}:${c}`;
        if (!tokenToId.has(tok)) {
          token = tok;
          break;
        }
      }
      idToToken.set(id, token);
      tokenToId.set(token, id);
    }
  }
  return { idToToken, tokenToId };
}

function tokensToIds(tokens: readonly string[], map: TokenMap): string[] {
  const out: string[] = [];
  for (const tok of tokens) {
    const id = map.tokenToId.get(tok);
    if (id !== undefined) out.push(id);
  }
  return out;
}

function idsToTokens(ids: readonly string[], map: TokenMap): string[] {
  const out: string[] = [];
  for (const id of ids) {
    const tok = map.idToToken.get(id);
    if (tok !== undefined) out.push(tok);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useFilterUrlSync() {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const profIds = useAppSelector(selectSelectedProfessionIds);
  const mgrIds = useAppSelector(selectSelectedManagerIds);
  const eqTypes = useAppSelector(selectSelectedEquipmentTypes);
  const professions = useAppSelector(selectProfessions);
  const managers = useAppSelector(selectManagers);

  const profMap = useMemo(() => buildProfTokenMap(professions), [professions]);
  const mgrMap = useMemo(() => buildMgrTokenMap(managers), [managers]);

  const profRaw = searchParams.get(PARAM_PROF);
  const mgrRaw = searchParams.get(PARAM_MGR);
  const eqRaw = searchParams.get(PARAM_EQ);

  useEffect(() => {
    const tokens = parseList(profRaw);
    // Defer until lookups are ready — otherwise an `?prof=` link would
    // briefly resolve to [] and clobber state before the SSE payload
    // arrives.
    if (tokens.length > 0 && profMap.tokenToId.size === 0) return;
    const ids = tokensToIds(tokens, profMap);
    if (!arraysEqual(ids, profIds)) dispatch(setProfessionFilters(ids));
  }, [profRaw, profIds, profMap, dispatch]);

  useEffect(() => {
    const tokens = parseList(mgrRaw);
    if (tokens.length > 0 && mgrMap.tokenToId.size === 0) return;
    const ids = tokensToIds(tokens, mgrMap);
    if (!arraysEqual(ids, mgrIds)) dispatch(setManagerFilters(ids));
  }, [mgrRaw, mgrIds, mgrMap, dispatch]);

  useEffect(() => {
    const list = parseList(eqRaw) as EquipmentTypes[];
    if (!arraysEqual(list, eqTypes)) dispatch(setEquipmentFilters(list));
  }, [eqRaw, eqTypes, dispatch]);
}

// ---------------------------------------------------------------------------
// Toggle setters — accept the same id shape callers already use; translate
// to/from URL tokens internally.
// ---------------------------------------------------------------------------

function toggleIdsInUrl(
  setSearchParams: ReturnType<typeof useSearchParams>[1],
  paramKey: string,
  id: string,
  map: TokenMap,
) {
  setSearchParams(
    (prev) => {
      const next = new URLSearchParams(prev);
      const currentTokens = parseList(next.get(paramKey));
      const currentIds = tokensToIds(currentTokens, map);
      const idx = currentIds.indexOf(id);
      const newIds = idx === -1 ? [...currentIds, id] : currentIds.filter((x) => x !== id);
      const newTokens = idsToTokens(newIds, map);
      if (newTokens.length) next.set(paramKey, newTokens.join(','));
      else next.delete(paramKey);
      return next;
    },
    { replace: true },
  );
}

export function useToggleProfessionFilter() {
  const [, setSearchParams] = useSearchParams();
  const professions = useAppSelector(selectProfessions);
  const map = useMemo(() => buildProfTokenMap(professions), [professions]);
  return useCallback(
    (id: string) => {
      if (map.idToToken.size === 0) return; // lookup not ready
      toggleIdsInUrl(setSearchParams, PARAM_PROF, id, map);
    },
    [setSearchParams, map],
  );
}

export function useToggleManagerFilter() {
  const [, setSearchParams] = useSearchParams();
  const managers = useAppSelector(selectManagers);
  const map = useMemo(() => buildMgrTokenMap(managers), [managers]);
  return useCallback(
    (id: string) => {
      if (map.idToToken.size === 0) return;
      toggleIdsInUrl(setSearchParams, PARAM_MGR, id, map);
    },
    [setSearchParams, map],
  );
}

function useClearUrlParam(paramKey: string) {
  const [, setSearchParams] = useSearchParams();
  return useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(paramKey);
        return next;
      },
      { replace: true },
    );
  }, [paramKey, setSearchParams]);
}

export function useClearProfessionFilters() {
  return useClearUrlParam(PARAM_PROF);
}

export function useClearManagerFilters() {
  return useClearUrlParam(PARAM_MGR);
}

export function useClearEquipmentFilters() {
  return useClearUrlParam(PARAM_EQ);
}

export function useToggleEquipmentFilter() {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (type: EquipmentTypes) => {
      // Equipment values are already readable — token == id, no translation.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const current = parseList(next.get(PARAM_EQ));
          const idx = current.indexOf(type);
          if (idx === -1) current.push(type);
          else current.splice(idx, 1);
          if (current.length) next.set(PARAM_EQ, current.join(','));
          else next.delete(PARAM_EQ);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
}
